
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    MdArrowBack,
    MdAdd,
    MdEdit,
    MdDelete,
    MdMoreVert,
    MdSave,
    MdClose
} from 'react-icons/md';
import './InternalNotes.css';
import '../pages/Dashboard.css';
import Swal from 'sweetalert2';
import ActionMenuPortal from '../components/common/ActionMenuPortal';
import { formatDate } from '../utils/dateUtils';

const InternalNotes = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    // Dummy Data for Notes
    const [notes, setNotes] = useState([
        {
            id: 1,
            note: "Urgent delivery required by Friday morning. Vendor confirmed availability.",
            date: "2023-11-15 10:30 AM",
            createdBy: "John Doe",
            editedBy: "John Doe"
        },
        {
            id: 2,
            note: "Quality check passed for the first batch of steel bars.",
            date: "2023-11-16 02:45 PM",
            createdBy: "Jane Smith",
            editedBy: "Jane Smith"
        }
    ]);

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingNote, setEditingNote] = useState(null);
    const [noteText, setNoteText] = useState('');
    const [openMenuId, setOpenMenuId] = useState(null);
    const [anchorRect, setAnchorRect] = useState(null);



    const toggleMenu = (id, e) => {
        e.stopPropagation();
        if (openMenuId === id) {
            setOpenMenuId(null);
            setAnchorRect(null);
        } else {
            setOpenMenuId(id);
            setAnchorRect(e.currentTarget.getBoundingClientRect());
        }
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (event.target.closest('.swal2-container')) return;
            if (event.target.closest('.action-portal-wrapper')) return;
            if (event.target.closest('.action-menu-btn')) return;

            setOpenMenuId(null);
            setAnchorRect(null);
        };

        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, []);

    const handleAddNote = () => {
        setEditingNote(null);
        setNoteText('');
        setIsFormOpen(true);
    };

    const handleEditNote = (note) => {
        setEditingNote(note);
        setNoteText(note.note);
        setIsFormOpen(true);
        setOpenMenuId(null);
    };

    const handleSaveNote = () => {
        if (!noteText.trim()) {
            Swal.fire('Error', 'Note content cannot be empty.', 'error');
            return;
        }

        if (editingNote) {
            setNotes(prev => prev.map(n =>
                n.id === editingNote.id
                    ? { ...n, note: noteText, editedBy: "Current User", date: formatDate(new Date()) }
                    : n
            ));
            Swal.fire('Success', 'Note updated successfully.', 'success');
        } else {
            const newNote = {
                id: Date.now(),
                note: noteText,
                date: formatDate(new Date()),
                createdBy: "Current User",
                editedBy: "Current User"
            };
            setNotes(prev => [newNote, ...prev]);
            Swal.fire('Success', 'Note added successfully.', 'success');
        }
        setIsFormOpen(false);
    };

    const handleDeleteNote = async (id) => {
        const result = await Swal.fire({
            title: 'Delete Note?',
            text: 'Are you sure you want to delete this note?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#dc2626',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'swal-delete-btn',
                cancelButton: 'swal-cancel-btn',
            },
        });

        if (result.isConfirmed) {
            setNotes(prev => prev.filter(n => n.id !== id));
            Swal.fire('Deleted!', 'Note has been deleted.', 'success');
            setOpenMenuId(null);
        }
    };

    return (
        <div className="dashboard internal-notes-page">
            <div className="page-header sticky-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <button onClick={() => navigate('/generate-requests')} className="back-btn">
                        <MdArrowBack />
                    </button>
                    <h2 className="section-title" style={{ margin: 0 }}>Internal Notes - Request #{id}</h2>
                </div>
                <button onClick={handleAddNote} className="add-note-btn">
                    <MdAdd size={20} />
                    Add Note
                </button>
            </div>

            {
                isFormOpen && (
                    <div className="note-form-container">
                        <div className="note-form-card">
                            <div className="form-header">
                                <h3>{editingNote ? 'Edit Note' : 'Add New Note'}</h3>
                                <button className="close-form-btn" onClick={() => setIsFormOpen(false)}>
                                    <MdClose size={24} />
                                </button>
                            </div>
                            <div className="form-body">
                                <textarea
                                    value={noteText}
                                    onChange={(e) => setNoteText(e.target.value)}
                                    placeholder="Enter your note here..."
                                    rows="5"
                                    className="note-textarea"
                                />
                            </div>
                            <div className="form-footer">
                                <button className="cancel-btn" onClick={() => setIsFormOpen(false)}>Cancel</button>
                                <button className="save-btn" onClick={handleSaveNote}>
                                    <MdSave /> {editingNote ? 'Update Note' : 'Save Note'}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            <div className="card-container-notes">
                <div className="table-container">
                    <table>

                        <thead>
                            <tr>
                                <th style={{ width: '150px' }}>Date</th>
                                <th>Note</th>
                                <th style={{ width: '150px' }}>Created By</th>
                                <th style={{ width: '150px' }}>Edited By</th>
                                <th style={{ width: '80px', textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {notes.map((note, index) => (
                                <tr key={note.id}>
                                    <td className="note-date">{formatDate(note.date)}</td>
                                    <td className="note-content">{note.note}</td>
                                    <td>{note.createdBy}</td>
                                    <td>{note.editedBy}</td>
                                    <td className="action-cell">
                                        <button
                                            className="action-menu-btn"
                                            onClick={(e) => toggleMenu(note.id, e)}
                                        >
                                            <MdMoreVert size={20} />
                                        </button>

                                        <ActionMenuPortal
                                            isOpen={openMenuId === note.id}
                                            anchorRect={anchorRect}
                                            onClose={() => setOpenMenuId(null)}
                                        >
                                            <div className="action-dropdown shadow-lg">
                                                <button className="action-item" onClick={() => handleEditNote(note)}>
                                                    <MdEdit size={16} /> Edit
                                                </button>
                                                <button
                                                    className="action-item delete"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteNote(note.id);
                                                    }}
                                                >
                                                    <MdDelete size={16} /> Delete
                                                </button>
                                            </div>
                                        </ActionMenuPortal>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Mobile View */}
                <div className="mobile-card-view">

                    {notes.map((note) => (
                        <div key={note.id} className="project-card">
                            <div className="card-header">
                                <div className="card-title">
                                    <span className="note-date">{formatDate(note.date)}</span>
                                </div>
                                <button
                                    className="action-menu-btn"
                                    onClick={(e) => toggleMenu(note.id, e)}
                                >
                                    <MdMoreVert size={20} />
                                </button>
                                <ActionMenuPortal
                                    isOpen={openMenuId === note.id}
                                    anchorRect={anchorRect}
                                    onClose={() => setOpenMenuId(null)}
                                >
                                    <div className="action-dropdown shadow-lg">
                                        <button className="action-item" onClick={() => handleEditNote(note)}>
                                            <MdEdit size={16} /> Edit
                                        </button>
                                        <button
                                            className="action-item delete"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteNote(note.id);
                                            }}
                                        >
                                            <MdDelete size={16} /> Delete
                                        </button>
                                    </div>
                                </ActionMenuPortal>
                            </div>
                            <div className="card-body">
                                <div className="note-text-mobile">
                                    {note.note}
                                </div>
                                <div className="card-row" style={{ marginTop: '1rem' }}>
                                    <span className="card-label">Created By:</span>
                                    <span className="card-value">{note.createdBy}</span>
                                </div>
                                <div className="card-row">
                                    <span className="card-label">Edited By:</span>
                                    <span className="card-value">{note.editedBy}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div >
    );
};

export default InternalNotes;
